
import { IAuthRepository, AuthSession, AuthEvent, SessionValidationResult } from '@/shared/contracts/repositories/IAuthRepository';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

// Helper functions for safe JSON access
function isValidMetadata(metadata: any): metadata is { success?: boolean; error_message?: string } {
  return metadata && typeof metadata === 'object';
}

function getMetadataProperty<T>(metadata: any, key: string, fallback: T): T {
  if (!isValidMetadata(metadata)) return fallback;
  return metadata[key] ?? fallback;
}

export class AuthRepository implements IAuthRepository {
  // Session management
  async createSession(user: User, session: Session): Promise<AuthSession> {
    try {
      console.log('➕ Criando sessão para usuário:', user.id);
      
      const sessionData = {
        id: session.access_token.slice(0, 32), // Use part of token as ID
        user_id: user.id,
        session_token: session.access_token,
        expires_at: new Date(session.expires_at! * 1000).toISOString(),
        created_at: new Date().toISOString(),
        last_accessed_at: new Date().toISOString(),
        ip_address: null, // Would be set from request context
        user_agent: null  // Would be set from request context
      };

      console.log('✅ Sessão criada com sucesso');
      return sessionData as AuthSession;
    } catch (error) {
      console.error('❌ Erro ao criar sessão:', error);
      throw error;
    }
  }

  async deleteSession(sessionId: string): Promise<boolean> {
    try {
      console.log('🗑️ Removendo sessão:', sessionId);
      console.log('✅ Sessão removida (simulado)');
      return true;
    } catch (error) {
      console.error('❌ Erro ao remover sessão:', error);
      return false;
    }
  }

  async validateSession(sessionToken: string): Promise<SessionValidationResult> {
    try {
      console.log('🔍 Validando sessão...');
      
      // Correct Supabase v2 API usage
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        return { isValid: false, error: error.message };
      }

      const session = data.session;
      const user = session?.user;

      if (!session || !user) {
        return { isValid: false, error: 'No valid session found' };
      }

      const now = Date.now() / 1000;
      if (session.expires_at && session.expires_at < now) {
        return { isValid: false, error: 'Session expired' };
      }

      console.log('✅ Sessão válida');
      return { isValid: true, session, user };
    } catch (error) {
      console.error('❌ Erro ao validar sessão:', error);
      return { isValid: false, error: 'Validation failed' };
    }
  }

  async refreshSession(sessionToken: string): Promise<Session | null> {
    try {
      console.log('🔄 Atualizando sessão...');
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Erro ao atualizar sessão:', error);
        return null;
      }

      const session = data.session;
      console.log('✅ Sessão atualizada');
      return session;
    } catch (error) {
      console.error('❌ Erro ao atualizar sessão:', error);
      return null;
    }
  }

  async revokeAllUserSessions(userId: string): Promise<boolean> {
    try {
      console.log('🚫 Revogando todas as sessões do usuário:', userId);
      
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) {
        console.error('❌ Erro ao revogar sessões:', error);
        return false;
      }

      console.log('✅ Todas as sessões revogadas');
      return true;
    } catch (error) {
      console.error('❌ Erro ao revogar sessões:', error);
      return false;
    }
  }

  async cleanupExpiredSessions(): Promise<number> {
    console.log('🧹 Limpando sessões expiradas...');
    return 0;
  }

  // Security & Audit - simplified implementations
  async logAuthEvent(event: Omit<AuthEvent, 'id' | 'created_at'>): Promise<AuthEvent> {
    try {
      const eventData = {
        id: crypto.randomUUID(),
        ...event,
        created_at: new Date().toISOString()
      };

      console.log('📝 Evento de auth registrado:', event.event_type);
      return eventData as AuthEvent;
    } catch (error) {
      console.error('❌ Erro ao registrar evento:', error);
      throw error;
    }
  }

  async getAuthHistory(userId: string, limit?: number): Promise<AuthEvent[]> {
    console.log('🔍 Buscando histórico de auth para usuário:', userId);
    return [];
  }

  async checkRateLimit(userId: string, action: string): Promise<boolean> {
    return true; // Simplified - always allow
  }

  async checkPermissions(userId: string, resource: string, action: string): Promise<boolean> {
    return true; // Simplified - always allow
  }

  // Base repository methods (not used for auth)
  async findById(id: string): Promise<AuthSession | null> {
    throw new Error('Not implemented for AuthRepository');
  }

  async findMany(filters?: Record<string, any>): Promise<AuthSession[]> {
    throw new Error('Not implemented for AuthRepository');
  }

  async create(data: any): Promise<AuthSession> {
    throw new Error('Use createSession instead');
  }

  async update(id: string, data: any): Promise<AuthSession> {
    throw new Error('Not implemented for AuthRepository');
  }

  async delete(id: string): Promise<boolean> {
    return this.deleteSession(id);
  }
}
